import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Menu,
    MenuItem,
    Stack,
    Typography,
    IconButton,
    TextField,
} from '@mui/material';
import LanguagePicker from '../../Shared/LanguagePicker';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import HelpIcon from '@mui/icons-material/Help';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import LogoutIcon from '@mui/icons-material/Logout';
import { ArrowBackIosNew } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import LocalizationProviderWithLanguages from '../../Shared/Localization/LocalizationProviderWithLanguages';
import { TimePicker } from '@mui/x-date-pickers';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { EmployeeMobileVovClient } from '../../../ApiOld/Api/ApiServerVov';
import { EditWorkTimeNotifyRequestDTO } from '../../../Api/Mobile/ApiMobileServerHook';
import { useSnackbar } from 'notistack';

export interface IMobileAvatarProps {
    initials: string | undefined;
    fullName: string | undefined;
    arrivalNotifications: Date | undefined;
    departureNotifications: Date | undefined;
    handleLogout: () => void;
    onChange(e: Boolean): void;
}

export default function MobileAvatar(props: IMobileAvatarProps) {
    const { initials, fullName, arrivalNotifications, departureNotifications, handleLogout, onChange } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const { authorizedMobileEmployeeApi } = useMobileApi(EmployeeMobileVovClient);
    const [anchorElAvatar, setAnchorElAvatar] = useState<null | HTMLElement>(null);
    const [currentMenu, setCurrentMenu] = useState<'main' | 'notifications'>('main');
    const [menuWidth, setMenuWidth] = useState<number | null>(null);
    const menuListRef = useRef<HTMLUListElement | null>(null);
    const [avatarContent, setAvatarContent] = useState<string>(initials || '');
    const [borderRadius, setBorderRadius] = useState<string>('50%');
    const [arrival, setArrival] = useState<Date | undefined>();
    const [departure, setDeparture] = useState<Date | undefined>();
    const isManuallySet = useRef(false);

    useEffect(() => {
        if (!isManuallySet.current) {
            if (arrivalNotifications !== undefined) {
                setArrival(arrivalNotifications);
            }
            if (departureNotifications !== undefined) {
                setDeparture(departureNotifications);
            }
        }
    }, [arrivalNotifications, departureNotifications]);

    const handleCloseAvatarMenu = useCallback(() => {
        setAnchorElAvatar(null);
        setCurrentMenu('main');
        onChange(false);
    }, [onChange]);

    const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElAvatar(event.currentTarget);
        onChange(true);
    };

    const handleSave = useCallback(async (arrival: Date | undefined, departure: Date | undefined) => {
        await authorizedMobileEmployeeApi.editWorkTimeNotify(new EditWorkTimeNotifyRequestDTO({
            workTimeNotifyFrom: arrival ?? undefined,
            workTimeNotifyTo: departure ?? undefined,
        }))
            .then(() => {
                enqueueSnackbar(t('NotificationSaved'), { variant: 'success' });
            })
            .catch((error) => {
                enqueueSnackbar(error.toString(), { variant: 'error' });
            });
    }, [authorizedMobileEmployeeApi, enqueueSnackbar, t]);

    const renderMenuItems = useMemo(() => {
        if (currentMenu === 'main') {
            return (
                <Box>
                    <MenuItem>
                        <Box pr={2} ml={-2}>
                            <LanguagePicker
                                showText
                                onLanguageChange={(e) => {
                                    navigate('/' + e + location.pathname.substring(location.pathname.indexOf('/', 1)));
                                    handleCloseAvatarMenu();
                                }}
                            />
                        </Box>
                    </MenuItem>
                    <MenuItem onClick={() => setCurrentMenu('notifications')}>
                        <Stack mx={0.5} direction={'row'} alignItems={'center'} spacing={1} my={0.5}>
                            <NotificationsActiveIcon />
                            <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>
                                {t('Notifications')}
                            </Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleCloseAvatarMenu();
                        navigate('support');
                    }}>
                        <Stack mx={0.5} direction={'row'} alignItems={'center'} spacing={1} my={0.5}>
                            <SupportAgentIcon />
                            <Typography
                                sx={{ fontSize: 13, fontWeight: 'bold' }}
                            >
                                {t('Support')}
                            </Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleCloseAvatarMenu();
                        window.open('https://stavario.com/cs', '_blank');
                    }}>
                        <Stack mx={0.5} direction={'row'} alignItems={'center'} spacing={1} my={0.5}>
                            <HelpIcon />
                            <Typography
                                sx={{ fontSize: 13, fontWeight: 'bold' }}
                            >
                                {t('Tutorials')}
                            </Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem onClick={() => {
                        handleCloseAvatarMenu();
                        handleLogout();
                    }}>
                        <Stack mx={0.5} direction={'row'} alignItems={'center'} spacing={1} my={0.5}>
                            <LogoutIcon />
                            <Typography
                                sx={{ fontSize: 13, fontWeight: 'bold' }}
                            >
                                {t('LogOut')}
                            </Typography>
                        </Stack>
                    </MenuItem>
                </Box>
            );
        } else if (currentMenu === 'notifications') {
            return (
                <Box>
                    <MenuItem
                        onClick={() => {
                            setCurrentMenu('main');
                        }}
                    >
                        <Stack mx={0.5} direction={'row'} alignItems={'center'} spacing={1} my={0.5}>
                            <ArrowBackIosNew />
                            <Typography sx={{ fontSize: 13, fontWeight: 'bold' }}>
                                {t('Back')}
                            </Typography>
                        </Stack>
                    </MenuItem>
                    <MenuItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <LocalizationProviderWithLanguages>
                                <TimePicker
                                    onChange={(newTime) => {
                                        isManuallySet.current = true;
                                        setArrival(newTime ?? undefined)
                                    }}
                                    onAccept={() => handleSave(arrival, departure)}
                                    value={arrival ?? null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            contentEditable={false}
                                            label={t('ArrivalNotifications')}
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {params.InputProps?.endAdornment}
                                                        {arrival && <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setArrival(undefined);
                                                                handleSave(undefined, departure);
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ height: 14, width: 14, color: 'black' }} />
                                                        </IconButton>}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProviderWithLanguages>
                        </Stack>
                    </MenuItem>
                    <MenuItem>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <LocalizationProviderWithLanguages>
                                <TimePicker
                                    onChange={(newTime) => {
                                        isManuallySet.current = true;
                                        setDeparture(newTime ?? undefined)
                                    }}
                                    onAccept={() => handleSave(arrival, departure)}
                                    value={departure || null}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            contentEditable={false}
                                            label={t('DepartureNotifications')}
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {params.InputProps?.endAdornment}
                                                        {departure && <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setDeparture(undefined);
                                                                handleSave(arrival, undefined);
                                                            }}
                                                        >
                                                            <CloseIcon sx={{ height: 14, width: 14, color: 'black' }} />
                                                        </IconButton>}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProviderWithLanguages>
                        </Stack>
                    </MenuItem>
                </Box>
            );
        }
    }, [currentMenu, t, navigate, location.pathname, handleCloseAvatarMenu, handleLogout, handleSave, arrival, departure]);
    useEffect(() => {
        if (anchorElAvatar) {
            setTimeout(() => {
                if (menuListRef.current) {
                    const width = menuListRef.current.offsetWidth;
                    setMenuWidth(width);
                }
            }, 0);
            setTimeout(() => {
                if (menuListRef.current) {
                    setAvatarContent(fullName || '');
                    setBorderRadius('8px');
                }
            }, 100);
        }
        else {
            setMenuWidth(null);
            setAvatarContent(initials || '');
            setBorderRadius('50%')
        }
    }, [anchorElAvatar, currentMenu, fullName, initials]);
    return (
        <Box>
            <Avatar
                onClick={handleAvatarClick}
                sx={{
                    px: 0,
                    borderRadius: anchorElAvatar ? '0%' : '50%',
                    width: menuWidth || 32,
                    height: 32,
                    background: '#404040',
                    fontSize: 14,
                    fontWeight: 'bold',
                    transition: 'width 0.3s, border-radius 0.3s',
                    position: 'absolute',
                    right: 16,
                    top: 8,
                    borderTopLeftRadius: borderRadius,
                    borderTopRightRadius: borderRadius,
                    alignContent: 'end',
                }}
            >
                {avatarContent}
            </Avatar>
            <Menu
                onClose={handleCloseAvatarMenu}
                anchorEl={anchorElAvatar}
                open={anchorElAvatar !== null}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                MenuListProps={{
                    ref: menuListRef,
                    sx: { textAlign: 'left' },
                }}
                PaperProps={{
                    sx: {
                        position: 'absolute',
                        width: 200,
                        right: 8,
                        borderTopLeftRadius: 0,
                        borderTopRightRadius: 0,
                    },
                }}
            >
                {renderMenuItems}
            </Menu>
        </Box>
    );
}
